<template>
  <div class="fileInput">
     <div class="fileUploadDropZone" :class="{'activeZone' : isActive}" @drop.prevent="onDrop" @dragenter.prevent="setActive" @dragover.prevent="setActive" @dragleave.prevent="setInactive" @mouseover="hoveringImage = true"
    @mouseout="hoveringImage = false">
        <div v-if="hoveringImage || hoveredFile == null" class="dropZoneContent">
           <p class="mt-2">Drag and drop file here</p>
           <p class="mt-2">or</p>
           <input type="file" ref="filesRef" :id="id" class="position-absolute invisible" v-on:change="onInputChange" :accept="acceptedFileTypeList.toString()"/>
           <label class="btn btn-info text-light" :for="id">Choose file</label>
           <div class="hoverContent">
              <p class="mt-2">Drag and drop file here</p>
           </div>
        </div>
        <div class="imageContainer" v-if="!hoveringImage && (hoveredFile != null && (typeof hoveredFile === 'object' && Object.keys(hoveredFile).length === 0) || typeof hoveredFile === 'string')">
          <img :src="getFileURL(hoveredFile)" alt="Uploaded Image" class="uploadedImage">
        </div>
     </div>
     <div class="fileList mx-2" v-show="fileList.length > 0">
        <div v-for="file of fileList" :key="file.id" class="p-1">
           <div>
              <span class="p-2">{{ file.name }}</span>
              <span class="removeFile" @click="removeFile(file)" title="Click to remove file"><i class="fa-solid fa-xmark fa-xs"></i></span>
           </div>
        </div>
     </div>

     <div v-if="isDuplicateFile">
        <div v-for="file of duplicateFiles" :key="file.id" class="p-1">
           <span class="p-2 small text-danger">Duplicate file: {{ file.name }} </span>
        </div>
     </div>

     <div v-if="isFileNotAccepted">
        <span class="p-2 small text-danger"><strong>File not accepted:</strong></span>
        <div v-for="file of notAcceptedFiles" :key="file.id" class="p-1">
           <span class="p-2 small text-danger"> {{ file.name }} </span>
        </div>
     </div>
  </div>
</template>


<script>
const events = ['dragenter', 'dragover', 'dragleave', 'drop'];

export default {
  props: {
  id: {
    type: String,
  },
  acceptedFileType: {
    type: String,
    default: '',
  },
  imageFile: {
    type: [Object, String],
    default: null,
    validator: (value) => {
      return (
        value === null ||
        value instanceof Object ||
        typeof value === 'string'
      );
    },
  },
},
  emits: ['fileListChange'],
  data() {
    return {
      isActive: false,
      inactiveTimeout: null,
      fileList: [], // for viewing purposes
      convertedFileList: [], // converted to base64 files, will be passed to parent component
      isDuplicateFile: false,
      duplicateFiles: [],
      acceptedFileTypeList: [],
      isFileNotAccepted: false,
      notAcceptedFiles: [],
      hoveredFile: null,
      hoveringImage: false
    };
  },
  mounted() {
    events.forEach((eventName) => {
      document.body.addEventListener(eventName, this.preventDefaults);
    });

    if (this.acceptedFileType !== '') {
      this.mapAcceptedFileTypes();
    }
    if (this.imageFile !== null) {
      if (typeof this.imageFile === 'object') {
        // If imageFile is an object, set the hoveredFile to its src property
        this.hoveredFile = this.imageFile.src;
      } else if (typeof this.imageFile === 'string') {
        // If imageFile is a string, directly assign it to the hoveredFile
        this.hoveredFile = this.imageFile;
      }
    }
  },
  unmounted() {
    events.forEach((eventName) => {
      document.body.removeEventListener(eventName, this.preventDefaults);
    });
  },
  methods: {
    preventDefaults(e) {
      e.preventDefault();
    },
    onDrop(e) {
      this.setInactive();
      this.addFiles(e.dataTransfer.files);
    },
    setActive() {
      this.isActive = true;
      clearTimeout(this.inactiveTimeout);
    },
    setInactive() {
      this.inactiveTimeout = setTimeout(() => {
        this.isActive = false;
      }, 50);
    },
    onInputChange(e) {
      this.addFiles(e.target.files);
      e.target.value = null;
    },
    addFiles(files) {
      const file = files[0]; // Get the first file from the files array

      if (this.isFileExisting(file) || !this.isFileAccepted(file)) {
        if (this.isFileExisting(file)) {
          this.isDuplicateFile = true;
          this.duplicateFiles.push({
            name: file.name,
          });
          setTimeout(() => {
            this.isDuplicateFile = false;
            this.duplicateFiles = [];
          }, 2500);
        }

        if (!this.isFileAccepted(file)) {
          this.isFileNotAccepted = true;
          this.notAcceptedFiles.push({
            name: file.name,
          });
          setTimeout(() => {
            this.isFileNotAccepted = false;
            this.notAcceptedFiles = [];
          }, 2500);
        }
      } else {
        this.fileList[0] = file;
        this.hoveredFile = file;
        this.convertedFileList.push(this.convertFileToBase64(file));
      }

      this.$emit('fileListChange', { type: this.id, files: this.fileList[0] });
    },
    removeFile(file) {
      const index = this.fileList.indexOf(file);
      if (index > -1) {
        this.fileList.splice(index, 1);
        this.convertedFileList.splice(index, 1);
      }

      this.hoveredFile = null;
      this.$emit('fileListChange', { type: this.id, files: this.convertedFileList });
    },
    convertFileToBase64(file) {
      // convert to base64
      let reader = new FileReader();
      let fileInfo = {};

      let fileName = file.name;
      let extType = fileName.substring(fileName.lastIndexOf('.'));

      reader.readAsDataURL(file);
      reader.onload = () => {
        fileInfo.encoded = reader.result.split(',')[1];
        fileInfo.name = fileName;
        fileInfo.extension = extType;
      };
      return fileInfo;
    },
    isFileExisting(file) {
      let exists = this.fileList.some((f) => {
        return this.fileInfo(f) === this.fileInfo(file);
      });

      return exists;
    },
    isFileAccepted(file) {
      if (this.acceptedFileTypeList.length > 0) {
        let fileType = file.name.substring(file.name.lastIndexOf('.'));
        let isAccepted = this.acceptedFileTypeList.some((type) => {
          return type === fileType;
        });

        return isAccepted;
      } else {
        return true;
      }
    },
    mapAcceptedFileTypes() {
      if (this.acceptedFileType !== '') {
        if (this.acceptedFileType.includes(',')) {
          let fileType = this.acceptedFileType.split(',');
          fileType.forEach((type) => {
            this.acceptedFileTypeList.push('.' + type.trim());
          });
        } else {
          this.acceptedFileTypeList.push('.' + this.acceptedFileType);
        }
      }
    },
    fileInfo(file) {
      return file.name + '-' + file.size + '-' + file.lastModified + '-' + file.type;
    },
    resetForm() {
      this.fileList = [];
      this.convertedFileList = [];
    },
    getFileURL(file) {
      if (file instanceof File) {
        return URL.createObjectURL(file);
      } else if (typeof file === 'string' && file.startsWith('https://')) {
        return file;
      }
      return '';
    },
  },
};
</script>

<style scoped>
/* .fileInput {
   background-color: #f4fbff;
} */

.fileUploadDropZone {
  /* color: #fff; */
  outline: 2px dashed #00a0c1;
  outline-offset: -10px;
  transition: outline-offset 0.2s ease-out, background-color 0.2s ease-out;
  text-align: center;
  padding: 1rem;
}

.activeZone {
  outline-offset: -8px;
  outline-color: #00a0c1;
  background-color: #f5f5f5;
}

.removeFile {
  color: red;
}

.removeFile:hover {
  cursor: pointer;
}

.fileList {
  border: 2px solid #f5f5f5;
  border-radius: 10px;
  /* border-color: #f5f5f5; */
  /* border-width: 2px; */
}

.uploadedImage {
  max-width: 100%;
  height: 120px;
}
</style>
